import { Table, TableColumnsType } from "antd";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { SSROptionsKeys } from "src/Modules/Flights/constants/JourneyDetails";
import { formulateName } from "src/utility";
import { getItemLabel, getItemPrice, getRowTotal, getSSRItems, SSRColumn, SSRRow, SSRSelectionLayoutProps } from "../utils";

type TableColumnsInput = {
  t: TFunction;
  columns: SSRColumn[];
  type: SSROptionsKeys;
  formatPrice: (value: number) => string;
};

const getTableColumns = ({ t, columns, type, formatPrice }: TableColumnsInput): TableColumnsType<SSRRow> => [
  {
    key: "traveller",
    title: t("Flights.details.ssr.traveller", { defaultValue: "Traveller" }),
    render: (_, { passenger, typeIndex }) => {
      const passengerType = t(`Flights.details.passengerType.${passenger.type.toLowerCase()}`);
      const name = formulateName(passenger.basicData?.firstName ?? passengerType);
      return (
        <div className="flex items-center gap-2">
          <span className="bg-background-primary text-font-inverted flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold">
            {name?.charAt(0)}
          </span>
          <div className="flex min-w-0 flex-col">
            <span className="text-font-default truncate text-sm font-semibold">{name}</span>
            <span className="text-font-subtle text-xs">
              {`${passengerType} ${typeIndex}`}
            </span>
          </div>
        </div>
      );
    },
  },
  ...columns.map<TableColumnsType<SSRRow>[number]>((column) => ({
    key: column.key,
    title: column.title,
    render: (_, row) => {
      const items = getSSRItems(row.passenger, column, type);
      if (!items.length) return <span className="text-font-minimal">—</span>;
      return (
        <div className="flex flex-col gap-1">
          {items.map((item) => (
            <div key={item.code} className="flex flex-col">
              <span className="text-font-default text-sm break-words">{getItemLabel(item, type)}</span>
              <span className="text-font-active text-xs font-semibold">{formatPrice(getItemPrice(item))}</span>
            </div>
          ))}
        </div>
      );
    },
  })),
  {
    key: "travellerTotal",
    align: "end",
    title: t("Flights.details.ssr.travellerTotal", { defaultValue: "Traveller total" }),
    render: (_, row) => {
      const total = getRowTotal(row.passenger, columns, type);
      if (!total) return <span className="text-font-minimal">-</span>;
      return <span className="text-font-default text-sm font-semibold">{formatPrice(total)}</span>;
    },
  },
];

const SSRSelectionTableWeb = ({ rows, columns, type, formatPrice, totalLabel, grandTotal }: SSRSelectionLayoutProps) => {
  const { t } = useTranslation();

  return (
    <Table<SSRRow>
      columns={getTableColumns({ t, columns, type, formatPrice })}
      dataSource={rows}
      pagination={false}
      size="small"
      tableLayout="fixed"
      classNames={{
        header: {
          cell: "bg-background-minimal! text-font-subtle! px-4! py-3! text-xs! font-medium! uppercase tracking-wide before:hidden!",
        },
        body:{
            row:"p-4!",
            cell:"p-4!",
            wrapper:"p-4!"
        }
      }}
      summary={() => (
        <Table.Summary.Row className="bg-background-informative">
          <Table.Summary.Cell index={0} colSpan={columns.length + 1}>
            <span className="text-font-default text-sm font-semibold">{totalLabel}</span>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={columns.length + 1} align="end">
            <span className="text-font-active text-base font-semibold">{formatPrice(grandTotal)}</span>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      )}
    />
  );
};

export default SSRSelectionTableWeb;
